import { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';

import { useToast } from '@aicon-dev/caas-ui';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCookieState } from 'ahooks';
import { isAxiosError } from 'axios';
import { addYears } from 'date-fns';
import Cookies from 'js-cookie';

import { Splash } from '~/components/ui/Splash';
import apiClient from '~/config/apiClient';
import { getFcmInfo } from '~/modules/cloud-msg/libs/utils/get-fcm-info';
import { useNavigate } from '~/router';

import { UserModel } from '../../user/libs/types';
import { logout } from '../libs/apis';

export interface AuthContextValues {
  token?: string;
  /**
   * If you want to delete this record from localStorage, you can use setState() or setState(undefined).
   *
   * @param value
   * @link https://ahooks.js.org/hooks/use-local-storage-state#api
   */
  setToken: (value: undefined | string) => void;
  isLoggedIn: boolean;
  userInfo?: UserModel;
  login: (token: string, options?: LoginOptions) => void;
  logout: () => Promise<void>;
  refetchUserInfo: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<UserModel, Error>>;
}

export const AuthContext = createContext<AuthContextValues>({
  isLoggedIn: false,
  login() {},
  setToken() {},
  async logout() {},
  async refetchUserInfo() {
    return {} as QueryObserverResult<UserModel, Error>;
  },
});

interface LoginOptions {
  preventNavigate?: boolean;
}

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  //   const [token, _setToken] = useLocalStorageState<string>('JWT_TOKEN', {
  //     listenStorageChange: true,
  //   });
  const [token, _setToken] = useCookieState('JWT_TOKEN', {
    expires: addYears(new Date(), 1),
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const isLoggedIn = useMemo(() => {
    return !!token;
  }, [token]);

  const setToken = (value?: string) => {
    _setToken(value, { expires: addYears(new Date(), 1) });
    // _setToken(value);
  };

  /**
   * axios에 토큰 정보를 새로 갱신합니다.
   */
  const refreshAxiosToken = () => {
    if (!token) return;
    apiClient.interceptors.request.use(async (request) => {
      request.headers!.Authorization = `Bearer ${token}`;
      return request;
    });
  };

  useEffect(() => {
    refreshAxiosToken();
  }, [token]);

  const login = (token: string, options?: LoginOptions) => {
    setToken(token);
    getFcmInfo();
    if (options?.preventNavigate) {
      return;
    }
    navigate('/', { replace: true });
  };

  const _logout = async () => {
    setToken(undefined); // 토큰 제거 방법 (참고: https://ahooks.js.org/hooks/use-local-storage-state#api)
    try {
      const fcmToken = Cookies.get('FCM_TOKEN');
      await logout(fcmToken);
    } catch (error) {
      console.log('로그아웃 API 호출 중 오류가 발생했습니다.', error);
    }
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  const {
    data: userInfo,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['auth/me', token],
    queryFn: async () => {
      refreshAxiosToken();
      const { data } = await apiClient.get<UserModel>(`/auth/me`);
      return data;
    },
    enabled: !!token,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    gcTime: Infinity, // 로그인 정보는 영구적으로 저장합니다.
    staleTime: 0, // 모든 fetch 요청을 받아들입니다.
    throwOnError(error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          toast.error('로그인 정보가 만료되어 로그아웃합니다.');
          _logout();
        } else {
          toast.error('로그인 정보를 불러올 수 없습니다.');
          _logout();
        }
      }
      return false;
    },
  });

  if (isLoading) {
    return <Splash />;
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isLoggedIn,
        userInfo,
        refetchUserInfo: refetch,
        login,
        logout: _logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
